import type { User, LoginCredentials, LoginResponse, LoginResult, MfaChallenge, RefreshTokenResponse, TipoUsuario } from './types';
import { apiClient } from './api/apiClient';
import { API_ENDPOINTS, getApiBaseUrl } from './api';
import { getCurrentTenantSubdomain } from './utils/tenantUtils';
import { toast } from '@/shared/hooks/use-toast';
import i18n from '@/i18n';

export type TenantLookupResult =
  | { status: 'found'; tenant: { tenantId: string; name: string; accountModel: string } }
  | { status: 'not-found' }
  | { status: 'error' };

const TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'user_data';
const TOKEN_EXPIRY_KEY = 'token_expiry';
const MFA_CHALLENGE_KEY = 'mfa_challenge';

function unwrapApiPayload(response: any): any {
  if (response?.data && typeof response.data === 'object') {
    if (response.data.data && typeof response.data.data === 'object') return response.data.data;
    return response.data;
  }
  return response;
}

export class AuthService {
  private static instance: AuthService;
  private refreshTimeoutId: number | null = null;

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  private constructor() {
    apiClient.setAuthTokenProvider(() => this.getToken());
    apiClient.setTokenExpiredHandler(() => this.handleTokenExpired());

    this.scheduleTokenRefresh();
  }

  private handleTokenExpired(): void {
    this.clearAuthData();

    try {
      toast({
        title: i18n.t('auth:authContext.sessionExpiredTitle'),
        description: i18n.t('auth:authService.tokenExpiredDescription'),
        variant: "destructive",
      });
    } catch {
      // toast may not be available
    }

    if (window.location.pathname !== '/login') {
      setTimeout(() => {
        window.location.href = '/login';
      }, 1000);
    }
  }

  async login(credentials: LoginCredentials): Promise<LoginResult> {
    const subdomain = getCurrentTenantSubdomain();
    const options = subdomain ? { headers: { 'X-Tenant-Subdomain': subdomain } } : undefined;
    const response = await apiClient.post<any>(API_ENDPOINTS.LOGIN, credentials, options);
    const payload = unwrapApiPayload(response);

    if (payload?.requiresMfa === true) {
      const challenge: MfaChallenge = {
        mfaSessionToken: payload.mfaSessionToken,
        mfaExpiresIn: payload.mfaExpiresIn,
        requiresSetup: !!payload.requiresSetup,
        expiresAtMs: Date.now() + (payload.mfaExpiresIn ?? 300) * 1000,
      };
      this.setMfaChallenge(challenge);
      return {
        requiresMfa: true,
        mfaSessionToken: challenge.mfaSessionToken,
        mfaExpiresIn: challenge.mfaExpiresIn,
        requiresSetup: challenge.requiresSetup,
      };
    }

    const auth = payload?.auth ?? payload;
    if (!auth?.token || !auth?.user) {
      throw new Error(i18n.t('auth:authService.invalidLoginResponse'));
    }

    this.completeLogin(auth);
    return { requiresMfa: false, auth };
  }

  async getTenantBySubdomain(subdomain: string): Promise<TenantLookupResult> {
    try {
      const url = `${getApiBaseUrl()}/public/tenant-by-subdomain/${encodeURIComponent(subdomain)}`;
      const res = await fetch(url, { headers: { 'Content-Type': 'application/json' } });
      if (res.status === 404) return { status: 'not-found' };
      if (!res.ok) return { status: 'error' };
      const body = await res.json();
      const tenant = (body?.data ?? body) as { tenantId: string; name: string; accountModel: string };
      if (!tenant?.tenantId) return { status: 'error' };
      return { status: 'found', tenant };
    } catch {
      return { status: 'error' };
    }
  }

  completeLogin(auth: LoginResponse): void {
    this.storeAuthData(auth);
    this.clearMfaChallenge();
    this.scheduleTokenRefresh();
  }

  getMfaChallenge(): MfaChallenge | null {
    const raw = sessionStorage.getItem(MFA_CHALLENGE_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as MfaChallenge;
      if (Date.now() >= parsed.expiresAtMs) {
        sessionStorage.removeItem(MFA_CHALLENGE_KEY);
        return null;
      }
      return parsed;
    } catch {
      sessionStorage.removeItem(MFA_CHALLENGE_KEY);
      return null;
    }
  }

  setMfaChallenge(challenge: MfaChallenge): void {
    sessionStorage.setItem(MFA_CHALLENGE_KEY, JSON.stringify(challenge));
  }

  clearMfaChallenge(): void {
    sessionStorage.removeItem(MFA_CHALLENGE_KEY);
  }

  async logout(): Promise<void> {
    this.clearAuthData();
    this.clearRefreshTimeout();
  }

  async refreshToken(): Promise<RefreshTokenResponse> {
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await apiClient.post<any>(API_ENDPOINTS.REFRESH, {
        refreshToken
      });

      let refreshData = response;

      if (response.data && typeof response.data === 'object' && 'token' in response.data) {
        refreshData = response.data;
      } else if (response.data && typeof response.data === 'object' && response.data.data) {
        refreshData = response.data.data;
      }

      if (refreshData.token) {
        this.setToken(refreshData.token);
        if (refreshData.refreshToken) {
          this.setRefreshToken(refreshData.refreshToken);
        }
        this.setTokenExpiry(Date.now() + (refreshData.expiresIn * 1000));
        this.scheduleTokenRefresh();
        return refreshData;
      }

      throw new Error('Invalid refresh response - no token in response');
    } catch (error: any) {
      throw error;
    }
  }

  async getProfile(): Promise<User> {
    try {
      const response = await apiClient.get<any>(API_ENDPOINTS.PROFILE);

      let userData = response;

      if (response.data && typeof response.data === 'object') {
        if ('id' in response.data || 'email' in response.data) {
          userData = response.data;
        } else if (response.data.data) {
          userData = response.data.data;
        }
      }

      const user: User = {
        id: userData.id,
        username: userData.username || userData.email,
        name: userData.name || userData.fullName || userData.username || userData.email,
        fullName: userData.fullName || userData.name || userData.username || userData.email,
        email: userData.email,
        roles: userData.roles || (userData.role ? [userData.role] : []),
        status: userData.status || 'active',
        lastAccess: userData.lastAccess,
        isActive: userData.isActive !== undefined ? userData.isActive : true,
        createdAt: userData.createdAt || new Date().toISOString(),
        theme: userData.theme,
        sidebarPreference: userData.sidebarPreference,
        preferredLanguage: userData.preferredLanguage,
        tenantId: userData.tenantId,
        tenantTipo: userData.tenantTipo,
        tipoUsuario: userData.tipoUsuario,
        mfaEnabled: userData.mfaEnabled === true,
        mfaEnforcedAt: userData.mfaEnforcedAt ?? null,
        isPlatformAdmin: userData.isPlatformAdmin === true,
        canAssumeView: userData.canAssumeView === true,
        actingTenantId: userData.actingTenantId ?? null,
        actingTenantName: userData.actingTenantName ?? null,
      };

      this.setUser(user);
      return user;
    } catch (error) {
      throw error;
    }
  }

  async validateToken(): Promise<boolean> {
    const token = this.getToken();
    if (!token) {
      return false;
    }

    try {
      await this.getProfile();
      return true;
    } catch {
      return false;
    }
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  setToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  setRefreshToken(refreshToken: string): void {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }

  getUser(): User | null {
    const userData = localStorage.getItem(USER_KEY);
    if (userData) {
      try {
        const user = JSON.parse(userData);
        return user;
      } catch {
        return null;
      }
    }
    return null;
  }

  setUser(user: User): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }

  getTokenExpiry(): number | null {
    const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
    return expiry ? parseInt(expiry, 10) : null;
  }

  setTokenExpiry(timestamp: number): void {
    localStorage.setItem(TOKEN_EXPIRY_KEY, timestamp.toString());
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    const expiry = this.getTokenExpiry();

    if (!token) {
      return false;
    }

    if (!expiry) {
      return true;
    }

    if (Date.now() >= (expiry + (60 * 60 * 1000))) {
      this.clearAuthData();
      return false;
    }

    return true;
  }

  isTokenExpiringSoon(): boolean {
    const expiry = this.getTokenExpiry();
    if (!expiry) {
      return true;
    }

    const fiveMinutes = 5 * 60 * 1000;
    return (expiry - Date.now()) < fiveMinutes;
  }

  hasRole(role: string): boolean {
    const user = this.getUser();
    return user?.roles?.includes(role) ?? false;
  }

  hasAnyRole(roles: string[]): boolean {
    const user = this.getUser();
    if (!user?.roles) {
      return false;
    }

    return roles.some(role => user.roles.includes(role));
  }

  getTipoUsuario(): TipoUsuario | undefined {
    return this.getUser()?.tipoUsuario;
  }

  isPlatformAdmin(): boolean {
    return this.getUser()?.isPlatformAdmin ?? false;
  }

  isAdmin(): boolean {
    return this.getUser()?.tipoUsuario === 'Admin';
  }

  isOperador(): boolean {
    return this.getUser()?.tipoUsuario === 'Operador';
  }

  isVisualizador(): boolean {
    return this.getUser()?.tipoUsuario === 'Visualizador';
  }

  isTenant(): boolean {
    const user = this.getUser();
    const m = user?.tenantTipo as string | undefined;
    // Visoes estritas: Root (Invexis) e infraestrutura e nao opera como tenant;
    // a operacao acontece assumindo a visao de uma conta (o tenantTipo do token
    // vira o da conta assumida).
    return m === 'MultiTenant' || m === 'Msp' || m === 'Tenant';
  }

  isStandAlone(): boolean {
    const user = this.getUser();
    const m = user?.tenantTipo as string | undefined;
    return m === 'Standalone' || m === 'StandAlone';
  }

  canManageUsers(): boolean {
    return this.isAdmin();
  }

  canManageClients(): boolean {
    return this.isTenant() && (this.isAdmin() || this.isOperador());
  }

  canEditDevices(): boolean {
    return this.isAdmin() || this.isOperador();
  }

  canEditAlerts(): boolean {
    return this.isAdmin() || this.isOperador();
  }

  private storeAuthData(authResponse: LoginResponse): void {
    try {
      if (!authResponse.token) {
        throw new Error('Token is required');
      }
      if (!authResponse.user) {
        throw new Error('User data is required');
      }

      this.setToken(authResponse.token);

      if (authResponse.refreshToken) {
        this.setRefreshToken(authResponse.refreshToken);
      }

      this.setUser(authResponse.user);

      const expiresIn = authResponse.expiresIn || 3600;
      this.setTokenExpiry(Date.now() + (expiresIn * 1000));
    } catch (error) {
      throw error;
    }
  }

  private clearAuthData(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
  }

  private scheduleTokenRefresh(): void {
    this.clearRefreshTimeout();

    const expiry = this.getTokenExpiry();
    if (!expiry || !this.isAuthenticated()) {
      return;
    }

    const refreshTime = expiry - Date.now() - (2 * 60 * 1000);

    if (refreshTime > 0) {
      this.refreshTimeoutId = window.setTimeout(() => {
        this.refreshToken().catch(() => {});
      }, refreshTime);
    }
  }

  private clearRefreshTimeout(): void {
    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId);
      this.refreshTimeoutId = null;
    }
  }
}

export const authService = AuthService.getInstance();

export const isAuthenticated = () => authService.isAuthenticated();
export const getUser = () => authService.getUser();
export const hasRole = (role: string) => authService.hasRole(role);
export const hasAnyRole = (roles: string[]) => authService.hasAnyRole(roles);
export const logout = () => authService.logout();

export const isAdmin = () => authService.isAdmin();
export const isOperador = () => authService.isOperador();
export const isVisualizador = () => authService.isVisualizador();
export const isTenant = () => authService.isTenant();
export const isStandAlone = () => authService.isStandAlone();
export const canManageUsers = () => authService.canManageUsers();
export const canManageClients = () => authService.canManageClients();
export const canEditDevices = () => authService.canEditDevices();
export const canEditAlerts = () => authService.canEditAlerts();
